import { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import StatusChip from "@/components/StatusChip";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";

import { Plus, HardHat } from "@phosphor-icons/react";

const COLS = [
  "draft", "approved", "material_issued", "cutting_started", "stitching_started",
  "quality_check_pending", "packaging_pending", "ready_for_dispatch", "completed",
];

export default function Production() {
  const [orders, setOrders] = useState([]);
  const [view, setView] = useState("kanban");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ product_sku: "", product_name: "", category: "apparel", quantity: 10, priority: "normal", instructions: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/production/orders");
      setOrders(data);
    } catch { toast.error("Could not load production orders"); }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = Object.fromEntries(COLS.map((c) => [c, []]));
    for (const o of orders) {
      const s = COLS.includes(o.status) ? o.status : "draft";
      g[s].push(o);
    }
    return g;
  }, [orders]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/production/orders/${id}`, { status }); toast.success("Status updated"); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/production/orders", form); toast.success("Production order created"); setShowForm(false); load(); }
    catch { toast.error("Create failed"); }
  };

  return (
    <div data-testid="production-page" className="page-fade">
      <PageHeader eyebrow="Module" title="Production & jobwork" description="Track apparel, jewellery, bags, footwear and accessories from cutting to dispatch — with kanban and table views." tint="clay" icon={HardHat}
        actions={
          <>
            <div className="tab-list" data-testid="production-view-switch">
              {["kanban", "table"].map((v) => (
                <button key={v} onClick={() => setView(v)} data-testid={`prod-view-${v}`} className={view === v ? "is-active" : ""}>{v}</button>
              ))}
            </div>
            <button className="btn-primary" onClick={() => setShowForm(true)} data-testid="new-production-btn">
              <Plus size={14} /> New order
            </button>
          </>
        }
      />

      {view === "kanban" ? (
        orders.length === 0 ? (
          <div className="card-soft"><EmptyState icon={HardHat} title="No production orders" description="Create your first order to see it flow through the kanban." /></div>
        ) : (
          <div className="grid grid-flow-col auto-cols-[300px] gap-4 overflow-x-auto pb-4">
            {COLS.map((col) => (
              <div key={col} className="min-h-[70vh] card-soft p-0" data-testid={`kanban-col-${col}`}>
                <div className="px-5 py-4 border-b border-border/70 flex items-center justify-between">
                  <div className="label-eyebrow capitalize">{col.replace(/_/g, " ")}</div>
                  <span className="text-xs font-mono text-muted-foreground tabular">{grouped[col].length}</span>
                </div>
                <div className="p-3 space-y-3">
                  {grouped[col].map((o) => (
                    <div key={o._id} className="rounded-lg border border-border/70 p-3.5 bg-background hover:border-border hover:shadow-[var(--shadow-xs)] transition-all" data-testid={`prod-card-${o.po_number}`}>
                      <div className="font-mono text-[11px] text-muted-foreground">{o.po_number}</div>
                      <div className="font-display text-[15px] font-semibold mt-1.5 leading-tight tracking-tight">{o.product_name || o.product_sku}</div>
                      <div className="text-xs text-muted-foreground mt-2 capitalize">{o.category} · Qty {o.quantity}</div>
                      <select
                        value={o.status}
                        onChange={(e) => setStatus(o._id, e.target.value)}
                        className="input-cell w-full mt-3"
                        data-testid={`prod-status-select-${o._id}`}
                      >
                        {["draft","approval_pending","approved","material_issued","cutting_started","cutting_completed","stitching_started","stitching_completed","quality_check_pending","quality_check_completed","packaging_pending","packaging_completed","ready_for_dispatch","completed","on_hold","cancelled"].map((s) => (
                          <option key={s} value={s}>{s.replace(/_/g," ")}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern">
            <thead><tr>
              <th>PO</th><th>Product</th><th>Category</th><th>Qty</th><th>Status</th><th>Created</th><th></th>
            </tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o._id} data-testid={`prod-row-${o.po_number}`}>
                  <td className="font-mono text-xs">{o.po_number}</td>
                  <td className="font-medium">{o.product_name || o.product_sku || "—"}</td>
                  <td className="text-sm text-muted-foreground capitalize">{o.category}</td>
                  <td className="tabular">{o.quantity}</td>
                  <td><StatusChip value={o.status} /></td>
                  <td className="text-xs text-muted-foreground">{fmtDate(o.created_at)}</td>
                  <td className="text-right whitespace-nowrap"><RecordActions entity="production_orders" record={o} frozen={["po_number"]} onChanged={load} /></td>
                </tr>
              ))}
              {!orders.length && <tr><td colSpan={6}><EmptyState icon={HardHat} title="No production orders" description="Create your first order to begin." /></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-lg" data-testid="new-production-form">
            <div className="label-eyebrow">New production order</div>
            <div className="display-3 mt-1 mb-6">Create</div>
            {[
              ["product_sku","Product SKU"],
              ["product_name","Product name"],
            ].map(([k,l]) => (
              <label key={k} className="block mb-5">
                <span className="label-eyebrow">{l}</span>
                <input required value={form[k]} onChange={(e)=>setForm({...form,[k]:e.target.value})}
                       className="input-underline"
                       data-testid={`prod-input-${k}`}/>
              </label>
            ))}
            <div className="grid grid-cols-2 gap-5">
              <label className="block">
                <span className="label-eyebrow">Category</span>
                <select value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})}
                        className="input-soft mt-2" data-testid="prod-input-category">
                  {["apparel","jewellery","bags","footwear","accessories","sample"].map(x=><option key={x} value={x}>{x}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="label-eyebrow">Quantity</span>
                <input type="number" value={form.quantity} onChange={(e)=>setForm({...form,quantity:Number(e.target.value)})}
                       className="input-underline"
                       data-testid="prod-input-quantity"/>
              </label>
            </div>
            <label className="block mt-5">
              <span className="label-eyebrow">Instructions</span>
              <textarea value={form.instructions} onChange={(e)=>setForm({...form,instructions:e.target.value})}
                        className="textarea-soft mt-2"
                        data-testid="prod-input-instructions"/>
            </label>
            <div className="flex gap-2 mt-8">
              <button type="button" onClick={()=>setShowForm(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="prod-submit">Create order</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
